import { withListOverflow } from "../../../v2/components/ListOverflow.jsx";
export const RentScheduleCard = withListOverflow(RentScheduleListOwner);
// src/features/leases/components/RentScheduleCard.jsx
//
// LEASE-RENT-SCHEDULE — collapsible "Rent schedule" accordion section.
// Shows a per-year table of contractual / abatement / net rent with escalation
// percentages. Highlights the current calendar year. Footer totals row.
// Currency toggle via displayCcy / rates props (same pattern as FinancialTermsCard).
//
// Props:
//   schedule     — output of buildRentSchedule() (array of row objects)
//   currentYear  — number (e.g. 2026) for row highlight
//   currency     — lease native currency code (ISO 4217)
//   displayCcy   — currently-selected display currency
//   rates        — FX rate map { USD:1, GBP:0.792, … } or null
//   open         — boolean (controlled by LeaseSummaryTab accordionStates)
//   onToggle     — () => void
//   isDerived    — boolean; when true the schedule is synthetic (show muted note)

import React, { useState } from "react";
import { AreaContractualRentDetail } from "./AreaContractualRentDetail.jsx";
import { MonthlyActualRentDetail } from "./MonthlyActualRentDetail.jsx";
import { monthlyPayableEvidence } from "../../../../shared/monthlyPayable.js";
import { annualAreaBasis } from "../../../shared/leaseAnnualHeadlines.js";
import { T } from "../../../app/tokens.js";
import { DisclosureSection } from "../../../app/DisclosureSection.jsx";
import { convertAmount } from "../../../utils/fxConvert.js";
import { getCurrencySymbol } from "../../../utils/format.js";
import { PaneHeading } from "../../../components/PaneHeading.jsx";
import { buildRentScheduleMonthlyDetails } from "../selectors/rentSchedule.js";
import { hasRentSegmentsForYear } from "../../../../shared/leaseAreaRent.js";
import { leaseMonthlyDisplay, hasRecordedRentSegments, EVEN_INSTALLMENT_LABEL, EVEN_INSTALLMENT_NOTE } from "../../../../shared/leaseMonthlyDisplay.js";

// ─── Formatting helpers ────────────────────────────────────────────────────

function fmtAmt(val, fromCcy, toCcy, rates) {
  if (val == null || !Number.isFinite(Number(val))) return "—";
  const sourceCcy = fromCcy || toCcy || "USD";
  const ccy = toCcy || sourceCcy;
  const converted = sourceCcy === ccy
    ? Number(val)
    : convertAmount(val, sourceCcy, ccy, rates);
  const displayCcy = converted == null ? sourceCcy : ccy;
  const n = converted == null ? Number(val) : converted;
  const sym = getCurrencySymbol(displayCcy);
  const prefix = sym !== displayCcy ? sym : "";
  return `${prefix}${Math.round(n).toLocaleString("en-US")} ${displayCcy}`;
}

function fmtPsf(val, fromCcy, toCcy, rates) {
  if (val == null || !Number.isFinite(Number(val))) return "—";
  const sourceCcy = fromCcy || toCcy || "USD";
  const ccy = toCcy || sourceCcy;
  const converted = sourceCcy === ccy
    ? Number(val)
    : convertAmount(val, sourceCcy, ccy, rates);
  const displayCcy = converted == null ? sourceCcy : ccy;
  const n = converted == null ? Number(val) : converted;
  const sym = getCurrencySymbol(displayCcy);
  const prefix = sym !== displayCcy ? sym : "";
  return `${prefix}${n.toFixed(2)} ${displayCcy}/sf`;
}

function sumRowsInCurrency(rows, field, displayCurrency, rates) {
  let total = 0;
  for (const row of rows) {
    const rowCurrency = (field === "contractual" ? row.contractualCurrency : null) || row.currency || displayCurrency;
    if (row[field] == null || !Number.isFinite(Number(row[field]))) return null;
    const amount = Number(row[field]);
    const converted = convertAmount(amount, rowCurrency, displayCurrency, rates);
    if (converted == null) return null;
    total += converted;
  }
  return total;
}

function fmtEsc(val) {
  if (val == null || !Number.isFinite(Number(val))) return "—";
  const s = Number(val) >= 0 ? "+" : "";
  return `${s}${Number(val).toFixed(1)}%`;
}

function formatYearCoverage(years) {
  const sortedYears = [...new Set(years)].sort((a, b) => a - b);
  if (sortedYears.length === 0) return "some years";

  const ranges = [];
  let start = sortedYears[0];
  let end = start;
  for (const year of sortedYears.slice(1)) {
    if (year === end + 1) {
      end = year;
      continue;
    }
    ranges.push(start === end ? `${start}` : `${start}–${end}`);
    start = end = year;
  }
  ranges.push(start === end ? `${start}` : `${start}–${end}`);
  return ranges.join(", ");
}

// ─── Partial-year label helpers ────────────────────────────────────────────

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function partialLabel(row) {
  if (!row.isPartial || row.partialFrom == null || row.partialTo == null) return null;
  const from = MONTH_ABBR[row.partialFrom - 1];
  const to   = MONTH_ABBR[row.partialTo   - 1];
  return `(${from}-${to} · ${row.months} mo)`;
}

// ─── Component ────────────────────────────────────────────────────────────

const COL_HEADS = ["Year", "$/SF", "Contractual", "Abatement", "Net rent", "Esc. %"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatWindowDate(date) {
  const iso = date instanceof Date ? date.toISOString().slice(0, 10) : String(date || "").slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}, ${match[1]}` : iso;
}

function RentScheduleYearDetails({ row, allowances, criticalDates, displayCurrency, rates, areas, currency, lease }) {
  const details = buildRentScheduleMonthlyDetails(row, allowances, criticalDates);
  const rowCurrency = row.currency || displayCurrency;
  const annualAbatement = Number(row.abatement);
  const hasAnnualAbatement = Number.isFinite(annualAbatement) && annualAbatement > 0;
  const exactCreditTotal = details.exactCredits.reduce((total, credit) => total + credit.amount, 0);
  const freeMonths = details.months.filter((month) => month.kind === "free");
  const windowLabels = details.windows.map((window) =>
    `${window.kind === "free" ? "Rent-free period" : "Rent-abatement window"}: ${formatWindowDate(window.start.iso)}–${formatWindowDate(window.end.iso)}`
  );
  const detailTitle = `Month by month · ${row.year}`;
  const hasSegments = hasRentSegmentsForYear(areas, row.year);
  const installment = leaseMonthlyDisplay(areas, row.year, 1, currency,
    row.isNotInSource || row.isFallback || row.isProjected ? null : row.contractual, rowCurrency, lease);
  const hasMonthly = monthlyPayableEvidence(lease).state !== "empty";

  return (
    <div
      id={`rent-schedule-month-details-${row.year}`}
      data-testid={`rent-schedule-month-details-${row.year}`}
      style={{ padding: "12px 14px", background: T.tint }}
    >
      <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 7 }}>
        {detailTitle}
      </div>
      {hasMonthly ? <MonthlyActualRentDetail lease={lease} row={row} displayCurrency={displayCurrency} rates={rates} />
        : hasSegments ? <AreaContractualRentDetail areas={areas} row={row} currency={currency}
        displayCurrency={displayCurrency} rates={rates} /> : (
        <div data-testid={`rent-schedule-annual-anchor-${row.year}`} style={{ fontSize: T.fsXs, color: T.mid }}>
          {row.isNotInSource || row.contractual == null
            ? "Annual rent is not recorded for this year."
            : row.isFallback
              ? "Annual rent is estimated; no annual schedule anchor is recorded for this year."
              : "Annual contractual and net rent are sourced from the recorded annual schedule."}
          {" "}{hasRecordedRentSegments(areas)
            ? "Monthly stepped coverage is not recorded for this year; no even installment is substituted."
            : installment.amount == null ? "Monthly rent is unavailable without a recorded annual contractual anchor."
              : <span data-testid={`rent-schedule-installment-${row.year}`}>{EVEN_INSTALLMENT_LABEL} · approximate.</span>}
        </div>
      )}
      {installment.approximate && <div>
        <div data-testid={`rent-schedule-month-strip-${row.year}`}
          style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(72px, 1fr))", gap: 5, overflowX: "auto", marginTop: 8 }}>
          {MONTHS.map((month, index) => <div key={month}
            data-testid={`rent-schedule-month-${row.year}-${index + 1}`} data-month-source="annual_installment"
            style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: "6px 3px", textAlign: "center", background: T.card, fontSize: T.fsXs, color: T.mid }}>
            <div>{month}</div>
            <strong style={{ display: "block", color: T.dark, marginTop: 3 }}>
              {fmtAmt(installment.amount, installment.currency, displayCurrency, rates)}
            </strong>
          </div>)}
        </div>
        <div style={{ marginTop: 8, fontSize: T.fsXs, color: T.mid }}>{EVEN_INSTALLMENT_NOTE}</div>
      </div>}
      {(details.state === "exact_months" || details.state === "dated_window") && <div
        data-testid={`rent-schedule-abatement-strip-${row.year}`}
        style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 5, marginTop: 8 }}>
        {details.months.map((month) => <div key={month.month}
          data-testid={`rent-schedule-abatement-month-${row.year}-${month.month}`} data-month-source={month.kind}
          style={{ padding: "6px 3px", fontSize: T.fsXxs, color: T.mid, border: `1px solid ${T.border}`, borderRadius: 4 }}>
          {month.label}: {month.kind === "free" ? "source-dated rent-free" : month.kind === "credit"
            ? `credit −${fmtAmt(month.creditAmount, rowCurrency, displayCurrency, rates)}`
            : month.kind === "window" ? "abatement window" : "No dated credit"}
        </div>)}
      </div>}

      {details.state === "annual_only" && (
        <div
          data-testid={`rent-schedule-annual-only-${row.year}`}
          style={{ marginTop: 8, fontSize: T.fsXs, color: T.mid }}
        >
          Year abatement: {fmtAmt(annualAbatement, rowCurrency, displayCurrency, rates)} - timing not dated for this year.
        </div>
      )}

      <>
          {(details.state === "exact_months" || details.state === "dated_window") && (
            <div
              data-testid={`rent-schedule-exact-summary-${row.year}`}
              style={{ marginTop: 9, fontSize: T.fsXs, color: T.mid }}
            >
              {details.state === "exact_months" && windowLabels.length > 0 && (
                <div data-testid={`rent-schedule-source-date-range-${row.year}`}>
                  Source date range{windowLabels.length === 1 ? "" : "s"}: {windowLabels.join("; ")}.
                </div>
              )}
              {details.exactCredits.length > 0 && (
                <div>
                  Dated abatement credit{details.exactCredits.length === 1 ? "" : "s"}:{" "}
                  {details.exactCredits.map((credit) => `${MONTHS[credit.month - 1]} ${row.year}`).join(", ")}
                  {" · "}{fmtAmt(exactCreditTotal, rowCurrency, displayCurrency, rates)} total.
                </div>
              )}
              {freeMonths.length > 0 && (
                <div data-testid={`rent-schedule-free-month-summary-${row.year}`}>
                  {freeMonths.length} source-dated rent-free month{freeMonths.length === 1 ? "" : "s"}:{" "}
                  {freeMonths.map((month) => month.label).join(", ")} {row.year}.
                </div>
              )}
            </div>
          )}

          {details.state === "dated_window" && (
            <>
              <div
                data-testid={`rent-schedule-window-summary-${row.year}`}
                style={{ marginTop: 9, fontSize: T.fsXs, color: T.mid }}
              >
                Dated period{windowLabels.length === 1 ? "" : "s"}: {windowLabels.join("; ")}
                {hasAnnualAbatement
                  ? <> · annual abatement total: {fmtAmt(annualAbatement, rowCurrency, displayCurrency, rates)}</>
                  : " · no annual abatement amount is recorded for this year."}
              </div>
              <div
                data-testid={`rent-schedule-no-monthly-split-${row.year}`}
                style={{
                  marginTop: 7, background: T.card, border: `1px dashed ${T.border}`,
                  borderRadius: 7, padding: "8px 11px", fontSize: T.fsXs, lineHeight: 1.5, color: T.mid,
                }}
              >
                Per-month amounts aren’t in the source. Shaded months show date coverage only; only complete
                calendar months are labeled rent-free. No partial-month split is inferred.
              </div>
            </>
          )}

          {details.state === "none" && !hasAnnualAbatement && (
            <div style={{ marginTop: 8, fontSize: T.fsXs, color: T.mid }}>
              No abatement this year.
            </div>
          )}
      </>
    </div>
  );
}

function RentScheduleListOwner({
  schedule = [],
  currentYear,
  currency = "USD",
  displayCcy,
  rates,
  open,
  onToggle,
  isDerived = false,
  leaseStartYear,
  leaseEndYear,
  allowances = [],
  areas = [],
  lease,
  criticalDates = [],
  // PDF-ABSTRACTION-MODEL-OVERHAUL: provenance for the rent schedule source.
  // Shape: { documentName, documentDate, citation, source, importedAt }
  rentScheduleSource = null,
}) {
  const [expandedYears, setExpandedYears] = useState(() => new Set());
  const effCcy    = displayCcy || currency;
  const rowCount  = schedule.length;
  const sourcedSchedule = schedule.filter((row) => !row.isNotInSource);
  const hasMissingSourceRows = schedule.some((row) => row.isNotInSource);

  // Label the exact years represented by sourced rows. This remains accurate
  // when source gaps occur between those years or at either end of the term.
  const sourcedYears = sourcedSchedule.map((row) => Number(row.year)).filter(Number.isFinite);
  const sourceCoverageLabel = formatYearCoverage(sourcedYears);
  const sourcedFirstYear = sourcedYears.length ? Math.min(...sourcedYears) : null;
  const sourcedLastYear = sourcedYears.length ? Math.max(...sourcedYears) : null;
  const hasSourceGaps = hasMissingSourceRows || (
    sourcedFirstYear != null &&
    ((leaseStartYear != null && sourcedFirstYear > leaseStartYear) ||
      (leaseEndYear != null && sourcedLastYear < leaseEndYear))
  );
  const totalLabel = hasSourceGaps && sourcedFirstYear != null
    ? `Total sourced (${sourceCoverageLabel})`
    : "Total";
  const totalNet  = schedule[0]?.isFallback
    ? null
    : (sourcedSchedule.length > 0 ? sumRowsInCurrency(sourcedSchedule, "net", effCcy, rates) : null);
  const totalContr = schedule[0]?.isFallback
    ? null
    : (sourcedSchedule.length > 0 ? sumRowsInCurrency(sourcedSchedule, "contractual", effCcy, rates) : null);
  const totalAbat  = schedule[0]?.isFallback
    ? null
    : (sourcedSchedule.length > 0 ? sumRowsInCurrency(sourcedSchedule, "abatement", effCcy, rates) : null);

  const titleNode = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <PaneHeading style={{ marginBottom: 0 }}>Rent schedule</PaneHeading>
      <span
        data-testid="rent-schedule-count-badge"
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          minWidth: 20, height: 18, padding: "0 6px", borderRadius: 999,
          background: rowCount > 0 ? T.blueSoft : T.lightGray,
          color:      rowCount > 0 ? T.blue     : T.mid,
          fontSize:   T.fsXs, fontWeight: T.fwSemibold,
        }}
      >
        {rowCount}
      </span>
      {isDerived && (
        <span style={{ fontSize: T.fsXs, fontWeight: T.fwRegular, color: T.mid, fontStyle: "italic" }}>
          estimated
        </span>
      )}
    </div>
  );

  return (
    <section
      data-testid="rent-schedule-card"
      style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 16px" }}
    >
      <DisclosureSection
        title={titleNode}
        open={open}
        onToggle={onToggle}
        buttonTestId="rent-schedule-toggle"
        buttonPadding="0"
      >
        <div style={{ paddingTop: 10 }}>
          {/* PDF-ABSTRACTION-MODEL-OVERHAUL: show document-level source attribution
              for rent schedules that originated from a PDF abstraction import.
              Displayed only when rentScheduleSource is populated with at least
              one of documentName, documentDate, or citation. */}
          {rentScheduleSource && (rentScheduleSource.documentName || rentScheduleSource.documentDate || rentScheduleSource.citation) && (
            <div
              data-testid="rent-schedule-source-attribution"
              style={{
                fontSize: T.fsXs, color: T.mid, fontStyle: "italic",
                marginBottom: rowCount > 0 ? 8 : 4,
                display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: T.fsXxs }}>📄</span>
              <span>
                {rentScheduleSource.documentName && (
                  <span data-testid="rent-schedule-source-doc">{rentScheduleSource.documentName}</span>
                )}
                {rentScheduleSource.documentDate && (
                  <span data-testid="rent-schedule-source-date">, {rentScheduleSource.documentDate}</span>
                )}
                {rentScheduleSource.citation && (
                  <span data-testid="rent-schedule-source-citation"> — {rentScheduleSource.citation}</span>
                )}
              </span>
            </div>
          )}
          {hasMissingSourceRows && (
            <div
              data-testid="rent-schedule-source-coverage"
              style={{
                fontSize: T.fsXs,
                color: T.mid,
                fontStyle: "italic",
                marginBottom: 8,
              }}
            >
              Source rent is available for {sourceCoverageLabel}; missing term years are not in the source.
            </div>
          )}
          {rowCount === 0 ? (
            <div style={{ fontSize: T.fsBase, color: T.mid, fontStyle: "italic" }}>
              No per-year cost rows yet. Add them in the lease edit modal.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                data-testid="rent-schedule-table"
                style={{
                  width: "100%", borderCollapse: "collapse",
                  fontSize: T.fsXs, tableLayout: "auto",
                }}
              >
                <thead>
                  <tr>
                    {COL_HEADS.map((h) => (
                      <th
                        key={h}
                        style={{
                          padding:       "10px 14px",
                          textAlign:     "right",
                          fontSize:      T.fsXxs,
                          fontWeight:    T.fwBold,
                          color:         "var(--v2-mid)",
                          textTransform: "uppercase",
                          letterSpacing: ".07em",
                          borderBottom:  "1px solid var(--v2-line)",
                          background:    "var(--v2-tint)",
                          whiteSpace:    "nowrap",
                          ...(h === "Year" ? { textAlign: "left" } : {}),
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((row) => {
                    const isCurrent = row.year === currentYear;
                    const isYearExpanded = expandedYears.has(row.year);
                    const rowCurrency = row.currency || currency;
                    const parentLabel = partialLabel(row);
                    const label = parentLabel && areas.some((area) => area.rentRates?.length)
                      ? `Parent term: ${parentLabel}` : parentLabel;
                    const rowStyle = {
                      background: isCurrent ? T.blueSoft : "transparent",
                    };
                    const cellBase = {
                      padding: "5px 10px",
                      borderBottom: `1px solid ${T.border}`,
                      color: T.dark,
                      fontWeight: isCurrent ? T.fwSemibold : T.fwRegular,
                    };
                    return (
                      <React.Fragment key={row.year}>
                      <tr data-testid={`rent-schedule-row-${row.year}`} style={rowStyle}>
                        <td style={{ ...cellBase, textAlign: "left" }}>
                          <button
                            type="button"
                            data-testid={`rent-schedule-expand-${row.year}`}
                            aria-label={`${isYearExpanded ? "Collapse" : "Expand"} rent details for ${row.year}`}
                            aria-expanded={isYearExpanded}
                            aria-controls={`rent-schedule-month-details-${row.year}`}
                            onClick={() => setExpandedYears((previous) => {
                              const next = new Set(previous);
                              if (next.has(row.year)) next.delete(row.year);
                              else next.add(row.year);
                              return next;
                            })}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 5,
                              border: 0, background: "transparent", color: T.dark,
                              padding: 0, font: "inherit", fontWeight: "inherit",
                              cursor: "pointer",
                            }}
                          >
                            <span aria-hidden="true" style={{ color: T.blue, width: 10, textAlign: "center" }}>
                              {isYearExpanded ? "▾" : "▸"}
                            </span>
                            {row.year}
                          </button>
                          {row.isNotInSource && (
                            <span
                              data-testid={`rent-schedule-not-in-source-${row.year}`}
                              style={{
                                marginLeft: 6,
                                fontSize: T.fsXxs,
                                color: T.mid,
                                fontStyle: "italic",
                                fontWeight: T.fwRegular,
                                whiteSpace: "nowrap",
                              }}
                            >
                              not in source
                            </span>
                          )}
                          {isCurrent && (
                            <span
                              data-testid="rent-schedule-current-year-badge"
                              style={{
                                marginLeft: 6, fontSize: T.fsXxs, fontWeight: T.fwBold,
                                padding: "1px 5px", borderRadius: 6,
                                background: T.blue, color: T.white,
                                verticalAlign: "middle",
                              }}
                            >
                              Now
                            </span>
                          )}
                          {row.isFallback && (
                            <span
                              data-testid="rent-schedule-fallback-badge"
                              style={{ marginLeft: 6, fontSize: T.fsXxs, color: T.mid, fontStyle: "italic" }}
                            >
                              est.
                            </span>
                          )}
                          {label && (
                            <span
                              data-testid="rent-schedule-partial-label"
                              style={{
                                marginLeft: 6,
                                fontSize: T.fsXxs,
                                color: T.mid,
                                fontStyle: "italic",
                                fontWeight: T.fwRegular,
                              }}
                            >
                              {label}
                            </span>
                          )}
                        </td>
                        <td style={{ ...cellBase, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {fmtPsf(lease && areas.some((area) => area.rentRates?.length) ? (
                            annualAreaBasis(lease, row.year, row) != null && row.contractual != null
                              ? row.contractual / annualAreaBasis(lease, row.year, row) : null
                          ) : row.ratePsf, row.contractualCurrency || rowCurrency, effCcy, rates)}
                        </td>
                        <td style={{ ...cellBase, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {fmtAmt(row.contractual, row.contractualCurrency || rowCurrency, effCcy, rates)}
                        </td>
                        <td style={{ ...cellBase, textAlign: "right", fontVariantNumeric: "tabular-nums", color: row.abatement > 0 ? T.forestGreen : T.dark }}>
                          {row.abatement > 0 ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                              {`−${fmtAmt(row.abatement, rowCurrency, effCcy, rates)}`}
                              {row.abatementSource && (
                                <span
                                  data-testid={`abatement-source-badge-${row.year}`}
                                  title={
                                    row.abatementSource === "yc"
                                      ? "Set on year-cost row"
                                      : row.abatementSource === "monthly_actual"
                                        ? "Derived: recorded contractual anchor minus signed actual payable"
                                        : "Derived from allowances table"
                                  }
                                  style={{
                                    fontSize: T.fsXxs, fontWeight: T.fwSemibold,
                                    padding: "1px 4px", borderRadius: 6,
                                    background: T.lightGray, color: T.mid,
                                    cursor: "default", fontVariantNumeric: "normal",
                                  }}
                                >
                                  {row.abatementSource === "yc" ? "schedule" : row.abatementSource === "monthly_actual" ? "derived" : "allowance"}
                                </span>
                              )}
                            </span>
                          ) : "—"}
                        </td>
                        <td style={{ ...cellBase, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: isCurrent ? T.fwBold : T.fwSemibold }}>
                          {fmtAmt(row.net, rowCurrency, effCcy, rates)}
                        </td>
                        <td style={{ ...cellBase, textAlign: "right", color: row.escPct != null && row.escPct > 0 ? T.mid : T.light }}>
                          {fmtEsc(row.escPct)}
                        </td>
                      </tr>
                      {isYearExpanded && (
                        <tr
                          data-testid={`rent-schedule-expanded-row-${row.year}`}
                          style={{ background: T.tint }}
                        >
                          <td colSpan={COL_HEADS.length} style={{ padding: 0, borderBottom: `1px solid ${T.border}` }}>
                            <RentScheduleYearDetails
                              lease={lease}
                              areas={areas}
                              currency={currency}
                              row={row}
                              allowances={allowances}
                              criticalDates={criticalDates}
                              displayCurrency={effCcy}
                              rates={rates}
                            />
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                {!schedule[0]?.isFallback && rowCount > 1 && (
                  <tfoot>
                    <tr data-testid="rent-schedule-totals-row">
                      <td
                        style={{
                          padding: "5px 10px", fontWeight: T.fwSemibold, color: T.dark,
                          borderTop: `2px solid ${T.borderDark}`, textAlign: "left",
                          fontSize: T.fsXs,
                        }}
                      >
                        {totalLabel}
                      </td>
                      <td style={{ padding: "5px 10px", borderTop: `2px solid ${T.borderDark}` }} />
                      <td
                        style={{
                          padding: "5px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                          fontWeight: T.fwSemibold, color: T.dark,
                          borderTop: `2px solid ${T.borderDark}`, fontSize: T.fsXs,
                        }}
                      >
                        {fmtAmt(totalContr, effCcy, effCcy, rates)}
                      </td>
                      <td
                        style={{
                          padding: "5px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                          fontWeight: T.fwSemibold, color: totalAbat > 0 ? T.forestGreen : T.dark,
                          borderTop: `2px solid ${T.borderDark}`, fontSize: T.fsXs,
                        }}
                      >
                        {totalAbat > 0 ? `−${fmtAmt(totalAbat, effCcy, effCcy, rates)}` : "—"}
                      </td>
                      <td
                        style={{
                          padding: "5px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums",
                          fontWeight: T.fwBold, color: T.dark,
                          borderTop: `2px solid ${T.borderDark}`, fontSize: T.fsXs,
                        }}
                      >
                        {fmtAmt(totalNet, effCcy, effCcy, rates)}
                      </td>
                      <td style={{ padding: "5px 10px", borderTop: `2px solid ${T.borderDark}` }} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </DisclosureSection>
    </section>
  );
}
